import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { adminFinanceApi } from '@/api/adminFinance'
import { formatMoney } from '@/components/billing/formatMoney'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { CONTRACT_TX_TYPES, type AdminContractTransaction } from '@/types/adminFinance'

export function AdminContractTransactionsTab() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [rows, setRows] = useState<AdminContractTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [type, setType] = useState('')
  const [from, setFrom] = useState<Dayjs | null>(null)
  const [to, setTo] = useState<Dayjs | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminFinanceApi.listContractTransactions({
        page,
        per_page: perPage,
        ...(type ? { type } : {}),
        ...(from ? { from: from.format('YYYY-MM-DD') } : {}),
        ...(to ? { to: to.format('YYYY-MM-DD') } : {}),
      })
      setRows(result.data)
      setTotal(result.total)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, perPage, type, from, to, t])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('admin.finance.type', 'Тип')}
          value={type}
          onChange={(e) => {
            setType(e.target.value)
            setPage(1)
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('admin.finance.allTypes', 'Все типы')}</MenuItem>
          {CONTRACT_TX_TYPES.map((tx) => (
            <MenuItem key={tx} value={tx}>
              {t(`admin.finance.contractTypes.${tx}`, tx)}
            </MenuItem>
          ))}
        </TextField>
        <DatePicker
          label={t('admin.finance.from', 'С')}
          value={from}
          onChange={(d) => {
            setFrom(d)
            setPage(1)
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label={t('admin.finance.to', 'По')}
          value={to}
          onChange={(d) => {
            setTo(d)
            setPage(1)
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.finance.date', 'Дата')}</TableCell>
              <TableCell>{t('admin.finance.teacher', 'Учитель')}</TableCell>
              <TableCell>{t('admin.finance.student', 'Ученик')}</TableCell>
              <TableCell>{t('admin.finance.type', 'Тип')}</TableCell>
              <TableCell align="right">{t('admin.finance.amount', 'Сумма')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('admin.finance.empty', 'Записей нет')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell>{tx.created_at ? formatDateDMY(tx.created_at) : '—'}</TableCell>
                  <TableCell>{tx.teacher?.name || (tx.teacher ? `#${tx.teacher.id}` : '—')}</TableCell>
                  <TableCell>{tx.student?.name || (tx.student ? `#${tx.student.id}` : '—')}</TableCell>
                  <TableCell>{t(`admin.finance.contractTypes.${tx.type}`, tx.type)}</TableCell>
                  {/* credit — пополнение счёта договора, debit — списание;
                      знак и цвет как в ContractFinanceSection и StudentFinancePage */}
                  <TableCell align="right" sx={{ color: tx.direction === 'credit' ? 'success.main' : 'error.main' }}>
                    {tx.direction === 'debit' ? '−' : '+'}
                    {formatMoney(tx.amount, tx.currency)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, page - 1)}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10))
          setPage(1)
        }}
        rowsPerPageOptions={[10, 15, 25, 50]}
        labelRowsPerPage={t('admin.finance.rowsPerPage', 'Строк на странице:')}
        labelDisplayedRows={({ from: f, to: tt, count }) => `${f}–${tt} ${t('admin.finance.of', 'из')} ${count}`}
      />
    </LocalizationProvider>
  )
}
